/**
 * Shared severity styling for the notification banner, bell, history modal,
 * and detail modal.
 *
 * Indigo is the base. It is deliberately a colour the user-facing app uses
 * nowhere else — blue means info, amber means warning, red means error, green
 * means success, purple flags internal accounts, sky marks report call-outs.
 * A routine product announcement must not read as an alert, so it gets its
 * own lane.
 *
 * `warning` and `critical` borrow the existing amber/red alert palette on
 * purpose: when a notification genuinely is urgent it should look like every
 * other urgent thing in the app.
 */

import { AlertCircle, AlertTriangle, Bell } from 'lucide-react';
import type { NotificationSeverity } from '../hooks/useNotifications';

export interface SeverityStyle {
  /** Full-width banner strip. */
  strip: string;
  /** Icon colour on the strip and in modal headers. */
  icon: string;
  /** CTA button on the strip. */
  button: string;
  /** Dismiss (X) button on the strip. */
  dismiss: string;
  /** Unread dot in the history list. */
  dot: string;
  /** lucide icon component for this severity. */
  Icon: typeof Bell;
}

export const SEVERITY_STYLES: Record<NotificationSeverity, SeverityStyle> = {
  info: {
    strip: 'bg-indigo-50 border-b border-indigo-300 text-indigo-900',
    icon: 'text-indigo-600',
    button: 'bg-indigo-600 hover:bg-indigo-700 text-white',
    dismiss: 'text-indigo-400 hover:text-indigo-700',
    dot: 'bg-indigo-600',
    Icon: Bell,
  },
  warning: {
    strip: 'bg-amber-50 border-b border-amber-400 text-amber-900',
    icon: 'text-amber-600',
    button: 'bg-amber-600 hover:bg-amber-700 text-white',
    dismiss: 'text-amber-500 hover:text-amber-800',
    dot: 'bg-amber-500',
    Icon: AlertTriangle,
  },
  critical: {
    strip: 'bg-red-50 border-b border-red-300 text-red-900',
    icon: 'text-red-600',
    button: 'bg-red-600 hover:bg-red-700 text-white',
    dismiss: 'text-red-400 hover:text-red-700',
    dot: 'bg-red-600',
    Icon: AlertCircle,
  },
};

/** Falls back to `info` for any unexpected value from the API. */
export function severityStyle(severity: string): SeverityStyle {
  return SEVERITY_STYLES[severity as NotificationSeverity] || SEVERITY_STYLES.info;
}

/** "Sep 2, 2026 · 2:14 PM" */
export function formatNotificationDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const date = d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const time = d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${date} · ${time}`;
  } catch {
    return '';
  }
}
